import { Box, Button, Grid, Typography } from "@material-ui/core";
import { ReactNode, useEffect, useState } from "react";
import * as yup from "yup";
import { AdviceStatuses, AdviceType, ApplicationClientRoles, InvestmentOptions, Product } from "../../../../api";
import {
  distributionOptionNames,
  incomeBands,
  investmentMethodOptions,
  investmentOptionNames,
  investmentPurposeOptions,
  pieTaxRates,
  ScreenProps,
  SendMethod,
  titles,
  wealthBands,
  YesNoOption,
} from "../../../../common/types";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { clearClient, saveClient, setMessage, upsertClientAsync } from "../../../../store/adviser";
import FormikWrapper from "../../../containers/FormikWrapper";
import { Colors, ProductIcon, SectionTitleText, SubtitleText, TitleText } from "../../../controls/helpers";
import RadioList from "../../../controls/inputs/RadioList";
import ConfirmSave from "../../ConfirmSave";
import ConfirmSend from "../../ConfirmSend";
import { formatDate, hasSelectedProductRoles } from "../../Helpers";
import Refer from "../../Refer";

enum FormMode {
  None,
  ConfirmSend,
  ConfirmSave,
  Refer,
}

const TextDetail = ({ label, children }: { label?: string; children?: ReactNode }) => (
  <>
    <Typography variant="subtitle2" style={{ color: Colors.Grey }}>
      {label}
    </Typography>
    <Typography variant="h6" style={{ color: Colors.Black, fontSize: "1.2rem" }}>
      {children}
    </Typography>
  </>
);

const ConfirmDetails = ({ prevRoute }: ScreenProps) => {
  const { client, upsertClientResult, advisers } = useAppSelector((state) => state.adviser);
  const { user } = useAppSelector((state) => state.oidc);
  const [formMode, setFormMode] = useState<FormMode>(FormMode.None);
  const dispatch = useAppDispatch();

  const getSendMethod = () => {
    if (client?.sendEmail && client?.sendSms) return SendMethod.EmailAndSms;
    else if (client?.sendEmail && !client?.sendSms) return SendMethod.Email;
    else if (!client?.sendEmail && client?.sendSms && client?.mobile && !client?.useNZFundsDisclosureTemplate) return SendMethod.Sms;
    else if (!client?.mobile || client?.useNZFundsDisclosureTemplate) return SendMethod.Email;
    else return SendMethod.None;
  };

  const initialValues = () => ({
    ...client,
    sendSms: client?.sendSms,
    sendEmail: client?.useNZFundsDisclosureTemplate || !client?.mobile || client?.sendEmail,
    saveAsDraft: false,
    sendMethod: getSendMethod(),
  });

  const refer = () => {
    setFormMode(FormMode.None);
    dispatch(setMessage({ text: "The application has been sent successfully." }));
    dispatch(clearClient());
  };

  useEffect(() => {
    if (upsertClientResult?.clientId) {
      setFormMode(client?.saveAsDraft ? FormMode.None : FormMode.Refer);

      if (client?.saveAsDraft) {
        dispatch(setMessage({ text: "The application has been saved successfully." }));
        dispatch(clearClient());
      }
    }
  }, [upsertClientResult]);

  return (
    <FormikWrapper
      initialValues={initialValues()}
      validationSchema={yup.object({ sendMethod: yup.mixed<SendMethod>().required("An invitation method is required").notOneOf([SendMethod.None], "An invitation method is required") })}
      onSubmit={(values) => {
        const _values = {
          ...values,
          sendEmail: values.sendMethod === SendMethod.EmailAndSms || values.sendMethod === SendMethod.Email,
          sendSms: values.sendMethod === SendMethod.EmailAndSms || values.sendMethod === SendMethod.Sms,
        };

        dispatch(saveClient(_values));

        if (_values.saveAsDraft) hasSelectedProductRoles(user, _values?.products) || advisers?.length === 1 ? dispatch(upsertClientAsync(_values!)) : setFormMode(FormMode.ConfirmSave);
        else setFormMode(FormMode.ConfirmSend);
      }}
    >
      {({ setFieldValue, values }) => (
        <>
          <TitleText hideMarginBottom>Confirm</TitleText>
          <SubtitleText hideMarginBottom>
            <Grid container justifyContent="space-between">
              <Grid item>
                {values.adviceStatus === AdviceStatuses.Skipped && (
                  <>
                    <span style={{ color: Colors.Black }}>
                      {values.adviceType === AdviceType.Comprehensive && "Comprehensive advice"}
                      {values.adviceType === AdviceType.Limited && "Limited advice"}
                      {values.adviceType === AdviceType.Information && "Information only"}
                    </span>
                    &emsp;
                  </>
                )}
                {values.adviceStatus === AdviceStatuses.Skipped ? "Product onboarding" : "Savings Advice Tool"}
              </Grid>
            </Grid>
          </SubtitleText>
          <Box>
            <SectionTitleText>Client</SectionTitleText>
            <Grid container spacing={3}>
              {values.salutation && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Title">{titles.find((title) => title.value === values.salutation)?.label}</TextDetail>
                </Grid>
              )}
              <Grid item md={3} sm={6} xs={12}>
                <TextDetail label="First name">{values.firstName}</TextDetail>
              </Grid>
              {values.middleName && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Middle name(s)">{values.middleName}</TextDetail>
                </Grid>
              )}
              <Grid item md={3} sm={6} xs={12}>
                <TextDetail label="Last name">{values.lastName}</TextDetail>
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <TextDetail label="Email">{values.email}</TextDetail>
              </Grid>
              {values.mobile && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Email">{values.mobile}</TextDetail>
                </Grid>
              )}
              {values.irdNumber && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="IRD number">{values.irdNumber}</TextDetail>
                </Grid>
              )}
              {values.dateOfBirth && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Date of birth">{formatDate(values.dateOfBirth)}</TextDetail>
                </Grid>
              )}
              {values.occupation && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Occupation">{values.occupation}</TextDetail>
                </Grid>
              )}
              {values.residentialAddress && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Residential address">{`${values.residentialAddress?.streetAddress}${values.residentialAddress?.suburb ? `, ${values.residentialAddress?.suburb}` : ""}${
                    values.residentialAddress?.city ? `, ${values.residentialAddress?.city}` : ""
                  }${values.residentialAddress?.postCode ? ` ${values.residentialAddress?.postCode}` : ""}${
                    values.residentialAddress?.country ? `, ${values.residentialAddress?.country}` : ""
                  }`}</TextDetail>
                </Grid>
              )}
              {values.postalAddress && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Postal address">{`${values.postalAddress?.streetAddress}${values.postalAddress?.suburb ? `, ${values.postalAddress?.suburb}` : ""}${
                    values.postalAddress?.city ? `, ${values.postalAddress?.city}` : ""
                  }${values.postalAddress?.postCode ? ` ${values.postalAddress?.postCode}` : ""}${values.postalAddress?.country ? `, ${values.postalAddress?.country}` : ""}`}</TextDetail>
                </Grid>
              )}
              {values.countryOfBirth && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Country of birth">{values.countryOfBirth}</TextDetail>
                </Grid>
              )}
              {!!values.citizenships?.length && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Citizenship(s)">{values.citizenships.join(", ")}</TextDetail>
                </Grid>
              )}
              {values.annualIncome && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Annual income">{incomeBands.find((i) => i.value === values.annualIncome)?.label}</TextDetail>
                </Grid>
              )}
              {values.estimatedTotalWealth && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Estimated total wealth">{wealthBands.find((i) => i.value === values.estimatedTotalWealth)?.label}</TextDetail>
                </Grid>
              )}
              {values.bankAccountName && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Bank account name">{values.bankAccountName}</TextDetail>
                </Grid>
              )}
              {values.bankAccountNumber && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Bank account number">{values.bankAccountNumber}</TextDetail>
                </Grid>
              )}
              {values.isNZTaxResident !== undefined && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="New Zealand tax resident">{values.isNZTaxResident ? YesNoOption.Yes : YesNoOption.No}</TextDetail>
                </Grid>
              )}
              {values.pieTaxRate && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="PIE tax rate">{pieTaxRates.find((rate) => rate.value === values.pieTaxRate)?.label || ""}</TextDetail>
                </Grid>
              )}
              {values.isUSPerson !== undefined && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="US Person">{values.isUSPerson ? YesNoOption.Yes : YesNoOption.No}</TextDetail>
                </Grid>
              )}
              {values.taxIdentificationNumber && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="US Tax Identification Number (TIN)">{values.taxIdentificationNumber}</TextDetail>
                </Grid>
              )}
              {values.isOtherTaxResident !== undefined && (
                <Grid item md={3} sm={6} xs={12}>
                  <TextDetail label="Tax resident of another country">{values.isOtherTaxResident ? YesNoOption.Yes : YesNoOption.No}</TextDetail>
                </Grid>
              )}
              {values.isOtherTaxResident !== undefined &&
                values.otherTaxCountries?.map((countryOfTaxResidence, index) => (
                  <Grid item xs={12} key={index}>
                    <Grid container spacing={3}>
                      <Grid item md={3} sm={6} xs={12}>
                        <TextDetail label="Country of tax residence">{countryOfTaxResidence.country}</TextDetail>
                      </Grid>
                      {countryOfTaxResidence.tin && (
                        <Grid item md={8} sm={6} xs={12}>
                          <TextDetail label="Tax Identification Number (TIN)">{countryOfTaxResidence.tin}</TextDetail>
                        </Grid>
                      )}
                      {countryOfTaxResidence.reason && (
                        <Grid item md={8} sm={6} xs={12}>
                          <TextDetail label="TIN unavailable reason">{countryOfTaxResidence.reason}</TextDetail>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
            </Grid>
            {!!values.countriesResided?.length && (
              <Box mb={3}>
                <SectionTitleText>Description of connections</SectionTitleText>
                <Grid container spacing={3}>
                  {values.countriesResided.map((countryResided, index) => (
                    <Grid item md={3} sm={6} xs={12} key={index}>
                      <TextDetail label={`Years living in ${countryResided.country}`}>{countryResided.yearsResided || "N/A"}</TextDetail>
                    </Grid>
                  ))}
                  {(values.assetsInNewZealand || values.assetsOverseas) && (
                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        <Grid item md={3} sm={6} xs={12}>
                          <TextDetail label="They have assets (e.g. property) in">
                            {values.assetsInNewZealand && `New Zealand${values.assetsOverseas ? ", " : ""}`}
                            {values.assetsOverseas && "Overseas countries"}
                          </TextDetail>
                        </Grid>
                        {values.assetsOther && (
                          <Grid item md={8} sm={6} xs={12}>
                            <TextDetail label="Asset details">{values.assetsOther}</TextDetail>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}
                  {(values.businessInterestInNewZealand || values.businessInterestOverseas) && (
                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        <Grid item md={3} sm={6} xs={12}>
                          <TextDetail label="They have business interests in">
                            {values.businessInterestInNewZealand && `New Zealand${values.businessInterestOverseas ? ", " : ""}`}
                            {values.businessInterestOverseas && "Overseas countries"}
                          </TextDetail>
                        </Grid>
                        {values.businessInterestDetails && (
                          <Grid item md={8} sm={6} xs={12}>
                            <TextDetail label="Business interests details">{values.businessInterestDetails}</TextDetail>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}
                  {(values.familyInNewZealand || values.familyOverseas) && (
                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        <Grid item md={3} sm={6} xs={12}>
                          <TextDetail label="They have family in">
                            {values.familyInNewZealand && `New Zealand${values.familyOverseas ? ", " : ""}`}
                            {values.familyOverseas && "Overseas countries"}
                          </TextDetail>
                        </Grid>
                        {values.familyDetails && (
                          <Grid item md={8} sm={6} xs={12}>
                            <TextDetail label="Details">{values.familyDetails}</TextDetail>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
            <Box mb={3}>
              <SectionTitleText>Products</SectionTitleText>
              <Grid container spacing={3}>
                {!!values.kiwiSaverApplication && (
                  <Grid item sm={3} xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Grid container spacing={3}>
                          <Grid item>
                            <ProductIcon product={Product.KiwiSaver} highlighted fullProductName />
                          </Grid>
                        </Grid>
                      </Grid>
                      {values.kiwiSaverApplication.investmentOption && (
                        <Grid item xs={12}>
                          <TextDetail label="Investment option">{investmentOptionNames[values.kiwiSaverApplication.investmentOption]}</TextDetail>
                        </Grid>
                      )}
                      {values.kiwiSaverApplication.investmentOption === InvestmentOptions.SelfSelect && (
                        <Grid item xs={12}>
                          <TextDetail label="Asset allocation">
                            {`Income: ${values.kiwiSaverApplication.income ?? 0}%`}
                            <br />
                            {`Inflation: ${values.kiwiSaverApplication.inflation ?? 0}%`}
                            <br />
                            {`Growth: ${values.kiwiSaverApplication.growth ?? 0}%`}
                          </TextDetail>
                        </Grid>
                      )}
                      {values.kiwiSaverApplication.isWithAnotherProvider !== undefined && (
                        <Grid item xs={12}>
                          <TextDetail label="With another provider">{values.kiwiSaverApplication.isWithAnotherProvider ? YesNoOption.Yes : YesNoOption.No}</TextDetail>
                        </Grid>
                      )}
                      {values.kiwiSaverApplication.isEligible !== undefined && (
                        <Grid item xs={12}>
                          <TextDetail label="Eligible for KiwiSaver">{values.kiwiSaverApplication.isEligible ? YesNoOption.Yes : YesNoOption.No}</TextDetail>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
                {!!values.wealthBuilderApplications?.length && (
                  <Grid item sm={3} xs={12}>
                    {values.wealthBuilderApplications?.map((application, index) => {
                      const jointClient = application.applicationClients?.find((c) => c.role === ApplicationClientRoles.Joint);

                      return (
                        <Grid container key={index} spacing={3}>
                          <Grid item xs={12}>
                            <Grid container spacing={3} style={{ paddingTop: index > 0 ? 30 : 0 }}>
                              <Grid item>
                                <ProductIcon product={Product.WealthBuilder} highlighted fullProductName />
                              </Grid>
                            </Grid>
                          </Grid>
                          {application.investmentOption && (
                            <Grid item xs={12}>
                              <TextDetail label="Investment option">{investmentOptionNames[application.investmentOption]}</TextDetail>
                            </Grid>
                          )}
                          {application.investmentOption === InvestmentOptions.SelfSelect && (
                            <Grid item xs={12}>
                              <TextDetail label="Asset allocation">
                                {`Income: ${application.income ?? 0}%`}
                                <br />
                                {`Inflation: ${application.inflation ?? 0}%`}
                                <br />
                                {`Growth: ${application.growth ?? 0}%`}
                              </TextDetail>
                            </Grid>
                          )}
                          {application.investmentPurpose && (
                            <Grid item xs={12}>
                              <TextDetail label="Investment purpose">{investmentPurposeOptions.find((ac) => ac.value === application.investmentPurpose)?.label ?? "Other"}</TextDetail>
                            </Grid>
                          )}
                          {application.investmentPurpose && !investmentPurposeOptions.some((ac) => ac.value === application.investmentPurpose) && (
                            <Grid item xs={12}>
                              <TextDetail label="Investment purpose reason">{application.investmentPurpose}</TextDetail>
                            </Grid>
                          )}
                          {application.investmentMethods && (
                            <Grid item xs={12}>
                              <TextDetail label="Investment method">{investmentMethodOptions.find((ac) => ac.value === application.investmentMethods)?.label}</TextDetail>
                            </Grid>
                          )}
                          {application.annualIncome && (
                            <Grid item xs={12}>
                              <TextDetail label="Estimated net annual income">{incomeBands.find((i) => i.value === application.annualIncome)?.label}</TextDetail>
                            </Grid>
                          )}
                          {application.estimatedTotalWealth && (
                            <Grid item xs={12}>
                              <TextDetail label="Estimated net wealth">{wealthBands.find((i) => i.value === application.estimatedTotalWealth)?.label}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.firstName && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant first name">{jointClient.firstName}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.middleName && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant middle name(s)">{jointClient.middleName}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.lastName && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant last name">{jointClient.lastName}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.mobile && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant mobile">{jointClient.mobile}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.email && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant email">{jointClient.email}</TextDetail>
                            </Grid>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
                {!!values.incomeGeneratorApplications?.length && (
                  <Grid item sm={3} xs={12}>
                    {values.incomeGeneratorApplications?.map((application, index) => {
                      const jointClient = application.applicationClients?.find((c) => c.role === ApplicationClientRoles.Joint);

                      return (
                        <Grid container key={index} spacing={3}>
                          <Grid item xs={12}>
                            <Grid container spacing={3} style={{ paddingTop: index > 0 ? 30 : 0 }}>
                              <Grid item>
                                <ProductIcon product={Product.IncomeGenerator} highlighted fullProductName />
                              </Grid>
                            </Grid>
                          </Grid>
                          {application.distributionOption && (
                            <Grid item xs={12}>
                              <TextDetail label="Distribution option">{distributionOptionNames[application.distributionOption]}</TextDetail>
                            </Grid>
                          )}
                          {application.investmentPurpose && (
                            <Grid item xs={12}>
                              <TextDetail label="Investment purpose">{investmentPurposeOptions.find((ac) => ac.value === application.investmentPurpose)?.label ?? "Other"}</TextDetail>
                            </Grid>
                          )}
                          {application.investmentPurpose && !investmentPurposeOptions.some((ac) => ac.value === application.investmentPurpose) && (
                            <Grid item xs={12}>
                              <TextDetail label="Investment purpose reason">{application.investmentPurpose}</TextDetail>
                            </Grid>
                          )}
                          {application.investmentMethods && (
                            <Grid item xs={12}>
                              <TextDetail label="Investment method">{investmentMethodOptions.find((ac) => ac.value === application.investmentMethods)?.label}</TextDetail>
                            </Grid>
                          )}
                          {application.annualIncome && (
                            <Grid item xs={12}>
                              <TextDetail label="Estimated net annual income">{incomeBands.find((i) => i.value === application.annualIncome)?.label}</TextDetail>
                            </Grid>
                          )}
                          {application.estimatedTotalWealth && (
                            <Grid item xs={12}>
                              <TextDetail label="Estimated net wealth">{wealthBands.find((i) => i.value === application.estimatedTotalWealth)?.label}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.firstName && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant first name">{jointClient.firstName}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.middleName && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant middle name(s)">{jointClient.middleName}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.lastName && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant last name">{jointClient.lastName}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.mobile && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant mobile">{jointClient.mobile}</TextDetail>
                            </Grid>
                          )}
                          {jointClient?.email && (
                            <Grid item xs={12}>
                              <TextDetail label="Joint applicant email">{jointClient.email}</TextDetail>
                            </Grid>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Grid>
            </Box>
            {values.useNZFundsDisclosureTemplate === true && (
              <SubtitleText hideMarginBottom>Your system disclosure statement will be emailed to your client when you click 'Send to client'.</SubtitleText>
            )}
            {values.mobile && (
              <Box mb={3}>
                <SectionTitleText>Onboarding</SectionTitleText>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <RadioList
                      horizontal
                      value={values.sendMethod}
                      name="sendMethod"
                      label="How would you like to send this invite?"
                      options={[
                        { label: `Email: ${values.email}`, value: SendMethod.Email },
                        ...(!values.useNZFundsDisclosureTemplate ? [{ label: `Mobile: ${values.mobile}`, value: SendMethod.Sms }] : []),
                        { label: values.useNZFundsDisclosureTemplate ? `Email & mobile: ${values.mobile}` : "Both", value: SendMethod.EmailAndSms },
                      ]}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
            <Grid container spacing={2} justifyContent="space-between">
              <Grid item>
                {!!prevRoute && (
                  <Button
                    variant="contained"
                    type="button"
                    disabled={formMode !== FormMode.None}
                    onClick={() => {
                      dispatch(saveClient(values));
                      prevRoute(values);
                    }}
                  >
                    Previous
                  </Button>
                )}
              </Grid>
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item>
                    <Button variant="contained" type="submit" disabled={formMode !== FormMode.None} onClick={() => setFieldValue("saveAsDraft", true)}>
                      Save
                    </Button>
                  </Grid>
                  {hasSelectedProductRoles(user, values?.products) && (
                    <Grid item>
                      <Button variant="contained" color="primary" type="submit" disabled={formMode !== FormMode.None}>
                        Send to client
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <ConfirmSend
            open={formMode === FormMode.ConfirmSend}
            cancel={() => setFormMode(FormMode.None)}
            ok={() => dispatch(upsertClientAsync(client!))}
            onlyKiwiSaver={!!values.kiwiSaverApplication && !values.wealthBuilderApplications?.length && !values.incomeGeneratorApplications?.length}
          />
          <ConfirmSave client={client!} open={formMode === FormMode.ConfirmSave} cancel={() => setFormMode(FormMode.None)} />
          <Refer ok={refer} cancel={refer} open={formMode === FormMode.Refer} firstName={values.firstName!} lastName={values.lastName!} mobile={values.mobile!} email={values.email!} />
        </>
      )}
    </FormikWrapper>
  );
};

export default ConfirmDetails;
